import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useFirebase } from 'react-redux-firebase'
import isEmpty from 'lodash/isEmpty'
import { useModalResize } from 'src/hooks/useModalResize'
import Modal from 'src/components/UI/Modal/Modal'
import PaymentProcessorModal from '../PaymentProcessorModal/PaymentProcessorModal'
import PriceListShared from 'training-lane-booking-shared/components/Purchase/PriceListShared/PriceListShared'
import PriceList from 'src/components/Purchase/PriceList/PriceList'
import Total from 'src/components/Total/Total'
import Confirm from 'src/components/UI/Confirm/Confirm'
import confirmClasses from 'src/components/UI/Confirm/Confirm.module.scss'
import {
    getYearMonthDate,
    isSpecialPricePeriod,
    roundToTwo
} from 'training-lane-booking-shared/utilities/functions'
import { hst } from 'training-lane-booking-shared/utilities/constants'
import textConstants from 'training-lane-booking-shared/utilities/text'
import purchaseModalClasses from './PurchaseModal.module.scss'

const PurchaseModal = ({
    now,
    roleId,
    credits,
    prices,
    pricesSpecial,
    cart,
    setModalState,
    updateCartInDatabase
}) => {
    const [disableSubmit, setDisableSubmit] = useState(true)

    const firebase = useFirebase()

    const foundSpecialPrice = isSpecialPricePeriod(
        roleId,
        getYearMonthDate(now),
        pricesSpecial
    )

    const height = useModalResize(
        450,
        330,
        window.innerWidth >= 480,
        foundSpecialPrice
    )

    useEffect(() => {
        setDisableSubmit(isEmpty(cart) || cart?.totalItems === 0)
    }, [cart])

    const handleChange = (itemData, addOrRemove) => {
        updateCartInDatabase({
            firebase,
            prices,
            itemTypeId: itemData.itemType.id,
            addOrRemove
        })
    }

    const handleSubmit = async () => {
        setModalState('paymentProcessor', { state: true })
    }

    const priceListSharedProps = {
        PriceList,
        roleId,
        prices,
        credits,
        foundSpecialPrice,
        handleChange
    }

    const subTotal = roundToTwo(cart?.grandTotal ?? 0)
    const grandTotal = roundToTwo((cart?.grandTotal ?? 0) * hst)
    const totalSavings = roundToTwo((cart?.totalSavings ?? 0) * hst)
    const calculatedHst = grandTotal - subTotal

    const totals = [
        {
            title: textConstants.SUBTOTAL,
            amount: subTotal
        },
        {
            title: textConstants.PURCHASE_SESSION_HST,
            amount: calculatedHst
        },
        {
            title: textConstants.GRAND_TOTAL,
            amount: grandTotal,
            type: 'big'
        },
        {
            title: textConstants.TOTAL_SAVINGS,
            amount: totalSavings,
            type: 'red'
        }
    ]

    return (
        <Modal
            className="purchase-modal-wrapper"
            name="purchase">
            <div className={purchaseModalClasses.purchaseModal}>
                <PaymentProcessorModal cart={cart} />
                <div className="align-center">
                    {textConstants.PURCHASE_MIX_AND_MATCH}.
                </div>
                <div className="align-center">
                    <a
                        href="#"
                        onClick={e => {
                            e.preventDefault()
                            setModalState('faq', {
                                state: true,
                                props: {
                                    targetSection: 'package-savings'
                                }
                            })
                        }}>
                        {textConstants.LEARN_MORE}
                    </a>
                </div>
                {foundSpecialPrice ? (
                    <div className={purchaseModalClasses.pricesSpecial}>
                        <h4 className={purchaseModalClasses.title}>
                            {foundSpecialPrice.title}
                        </h4>
                        <a
                            href="#"
                            onClick={e => {
                                e.preventDefault()
                                setModalState('pricesSpecial', { state: true })
                            }}>
                            {textConstants.LEARN_MORE}
                        </a>
                    </div>
                ) : null}
                <div
                    className={purchaseModalClasses.scrollable}
                    style={{ height }}>
                    <PriceListShared {...priceListSharedProps} />
                </div>
                <div className={purchaseModalClasses.totals}>
                    {totals.map((total, i) => {
                        const { title, amount, type } = total

                        return (
                            <Total
                                key={i}
                                type={type}
                                title={title}
                                amount={amount}
                            />
                        )
                    })}
                </div>
                <Confirm
                    buttons={[
                        {
                            buttonClasses:
                                confirmClasses.confirmButtonProceedSafe,
                            disabled: disableSubmit,
                            handleFn: handleSubmit,
                            label: textConstants.CHECKOUT
                        }
                    ]}
                />
            </div>
        </Modal>
    )
}

const mapState = state => ({
    now: state.utils.now,
    roleId: state.firebase.profile?.token?.claims?.roleId,
    credits: state.firebase.profile.credits,
    prices: state.firestore.data.prices,
    pricesSpecial: state.firestore.ordered.pricesSpecial,
    cart: state.cart.cart
})

const mapDispatch = dispatch => ({
    setModalState: (modalName, payload) =>
        dispatch.utils.setModalState(modalName, payload),
    updateCartInDatabase: payload => dispatch.cart.updateCartInDatabase(payload)
})

export default connect(mapState, mapDispatch)(PurchaseModal)
